<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
              <h2
                class="brand-text"
                :style="{ color: appColor }"
              >
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                :style="{ color: appColor }"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                :style="{ color: appColor }"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      :style="{ '--app-color': appColor }"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main test"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
/* eslint-disable no-plusplus */
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import {
  provide, computed, ref, onMounted,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import navMenuItems from '@/navigation/vertical'
import useJwt from '@/auth/jwt/useJwt'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    submenu: [],
    navMenuItems: [],
  }),
  created() {
    const sso = process.env.VUE_APP_USING_SSO === 'true'
    const queryParams = sso ? {
      params: {
        platform_id: process.env.VUE_APP_PLATFORM_ID,
      },
    } : {}
    useJwt.getPrivilegeUserPlatform(queryParams).then(res => {
      const navall = []
      const navchild = []
      const navbars = res.data.data.privilege_category
      let chld = 0
      for (let z = 0; z < navbars.length; z++) {
        const nav = []
        nav.header = navbars[z].name
        navall.push({ ...nav })
        for (let i = 0; i < navbars[z].privilege_category.length; i++) {
          if (navbars[z].privilege_category[i].parent_id !== 13) {
            if (navbars[z].privilege_category[i].level === 2) {
              for (let x = 0; x < navbars[z].privilege_category[i].privilege_category.length; x++) {
                if (navbars[z].privilege_category[i].privilege_category[x].level === 3) {
                  navchild[chld] = {
                    parent_id: navbars[z].privilege_category[i].privilege_category[x].parent_id,
                    title: navbars[z].privilege_category[i].privilege_category[x].name,
                    route: navbars[z].privilege_category[i].privilege_category[x].url,
                    icon: navbars[z].privilege_category[i].privilege_category[x].icon,
                  }
                  chld++
                }
              }
              const nav = []
              nav.title = navbars[z].privilege_category[i].name
              nav.icon = navbars[z].privilege_category[i].icon

              const result = navchild.filter(id => id.parent_id === navbars[z].privilege_category[i].id)
              if (result.length !== 0) {
                nav.children = result
              }
              nav.route = navbars[z].privilege_category[i].url
              navall.push({ ...nav })
            }
          }
        }
      }
      // console.log(navall)
      this.navMenuItems = navall
      localStorage.setItem('navbar', JSON.stringify(navall))
      // console.log(this.navMenuItems)
    })
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const appLogoImage = ref('')
    const appColor = ref('')
    const appName = ref($themeConfig.app.appName)

    onMounted(async () => {
      await useJwt.getProfile().then(response => {
        const { companies } = response.data.data
        appLogoImage.value = companies.logo
        appColor.value = `#${companies.partners.color !== null ? companies.partners.color.slice(2) : null}`
      })
    })

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      appColor,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
.main-menu.menu-light .navigation > li.active > a {
    background: var(--app-color);
    box-shadow: 0 0 10px 1px var(--app-color);
    border-radius: 4px;
}
</style>
